import { Injectable } from '@jkhy/frontend-utils/di';
import {
    AchBanksClient,
    AchBatchFilterModelDto,
    AchBatchModelDto,
    AchBatchesClient,
    AchClient,
    AchCompaniesClient,
    AchFileActivityClient,
    AchFileActivityFilterModelDto,
    AchFilterAmountTypeDto,
    AchFilterDateTypeDto,
    AchMasterRecipientClient,
    AchPaymentFilterModelDto,
    AchPaymentModelDto,
    AchPaymentsClient,
    AchRecipientFilterModelDto,
    AchRecipientsClient,
    AchSecCodeTypeDto,
    AchSettingsClient,
    AchTemplateStatusTypeDto,
    ChildSupportPayment2Dto,
    CompanyAccountsClient,
    CreateAchChildSupportTemplateRequestDto,
    GetAchChildSupportTemplateResponseDto,
    GetAchNotificationOfChangeDownloadRequestDto,
    GetAchReturnDownloadRequestDto,
    AchRecipientDto,
    GetMasterRecipientsRequestDto,
    GetMasterRecipientsResponseDto,
    HolidaysClient,
    OutPutTypeDto,
    SaveableMasterRecipientDto,
    SearchAchChildSupportTemplateRequestDto,
    SearchAchInternationalTemplateRequestDto,
    SearchAchNotificationOfChangeDownloadRequestDto,
    SearchAchNotificationOfChangeRequestDto,
    SearchAchReturnDownloadRequestDto,
    SearchAchTemplateBaseResponseDto,
    SecCodesClient,
    UpdateAchChildSupportTemplateRequestDto,
} from '@treasury/api/channel';
import { CutoffTimes } from '../channel/types';
import { SameDayAchSettings, getDefaultValueDate } from '../channel/types/ach';
import { CompanyAccountService } from '../company/company-account.service';
import {
    AchBatchFilter,
    AchCompany,
    AchFile,
    AchFileActivity,
    AchFileActivityFilter,
    AchFileError,
    AchNoticeOfChange,
    AchPayment,
    AchRecipientRecord,
    AchReturnActivity,
    AchReturnFilter,
    AchSource,
    AchTemplate,
    MasterRecipient,
    OffsetAccount,
} from './';
import { downloadBlob } from '../utilities/file-handling';
import { AchPaymentTotals } from './ach-payment-totals.entity';
import { ChildSupportPayment } from './child-support-payment.entity';
import { ChildSupportTemplate } from './child-support-template.entity';

@Injectable()
export class AchService {
    constructor(
        private readonly achBanksClient: AchBanksClient,
        private readonly batchClient: AchBatchesClient,
        private readonly companyClient: AchCompaniesClient,
        private readonly companyAccountsClient: CompanyAccountsClient,
        private readonly secCodesClient: SecCodesClient,
        private readonly achPaymentsClient: AchPaymentsClient,
        private readonly achSettingsClient: AchSettingsClient,
        private readonly holidaysClient: HolidaysClient,
        private readonly achRecipientsClient: AchRecipientsClient,
        private readonly achClient: AchClient,
        private readonly achFileActivityClient: AchFileActivityClient,
        private readonly companyAccountService: CompanyAccountService,
        private readonly achMasterRecipientClient: AchMasterRecipientClient
    ) {}

    public async getAchCutoffTimes() {
        const dto = (await this.companyAccountsClient.companyAccountsGetCutoffTimes2('SameDayAch', {
            maxAgeInSeconds: 600,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        })) as unknown as any;

        return dto.data;
    }

    public async getNextAvailableAchDay() {
        const holidays = (
            await this.holidaysClient.holidaysGet({ maxAgeInSeconds: 60000 })
        ).data.map(h => ({
            date: new Date(h.date),
        }));
        const sameDayAchSettings = (
            await this.companyAccountsClient.companyAccountsGetCutoffTimes2('SameDayAch', {
                maxAgeInSeconds: 600,
            })
        ).data;
        const allowSameDayPayments = sameDayAchSettings.processingCutoff?.allowSameDay ?? false;
        const cutoffTimes = await this.companyAccountService.getCutoffTimes();

        const date = getDefaultValueDate(
            allowSameDayPayments,
            cutoffTimes as CutoffTimes,
            holidays,
            sameDayAchSettings as SameDayAchSettings
        );
        // This check for $TODAY can be removed wihen Omega reaches EOL and the 'getDefaultValueDate' function is updated to return an ISO date string
        return date === '$TODAY' ? new Date() : new Date(date);
    }

    public async getSettings() {
        return (
            await this.achSettingsClient.achSettingsGet({
                maxAgeInSeconds: 6000,
            })
        ).data;
    }

    public async getTemplates(filters: AchBatchFilter) {
        const dtos = (
            await this.batchClient.achBatchesPostAll(filters.toDto() as AchBatchFilterModelDto, {
                maxAgeInSeconds: 600,
            })
        ).data;
        return dtos.map(dto => new AchTemplate(dto));
    }

    public async searchInternationalTemplates(filters: AchBatchFilter) {
        const requestDto = this.toInternationalTemplateRequest(filters);
        const response = (
            await this.achClient.achTemplateSearchInternational(requestDto, {
                maxAgeInSeconds: 600,
            })
        ).data;
        return (response.templates ?? []).map(
            t => new AchTemplate(this.mapTemplateResponseToBatchDto(t))
        );
    }

    public async getTemplateById(id: string) {
        const dto = (
            await this.batchClient.achBatchesGet(Number.parseInt(id), { maxAgeInSeconds: 600 })
        ).data;
        return new AchTemplate(dto.batch);
    }

    public async deleteTemplate(id: number) {
        return this.batchClient.achBatchesDelete({
            achBatchId: id,
            securityMessage: {
                actionType: 'Delete Batch',
                hasAlternate: false,
                challengeMethodTypeId: 0,
            },
            id: 0,
            updatedDate: '',
        });
    }

    public async getOffsetAccountsByACHCompanyId(id: number) {
        const dtos = (
            await this.companyClient.achCompaniesOffsetAccounts(id, undefined, {
                maxAgeInSeconds: 600,
            })
        ).data;
        return dtos.map(d => new OffsetAccount(d));
    }

    public async getAchCompanies() {
        const dtos = (await this.companyClient.achCompaniesGet({ maxAgeInSeconds: 600 })).data;
        return dtos.map(c => new AchCompany(c));
    }

    public async getSecCodes() {
        const response = await this.secCodesClient.secCodesGet({ maxAgeInSeconds: 600 });
        if (!response.data.includes('All')) {
            response.data.unshift('All');
        }
        return response.data;
    }

    public async getPaymentById(id: string) {
        const dto = (await this.achPaymentsClient.achPaymentsGet(Number.parseInt(id))).data;
        return new AchPayment(dto.payment);
    }

    public async createPayment(
        template: AchTemplate,
        applyUpdatesToBatch: boolean,
        duplicatePreventionId: string
    ) {
        const dto = await this.achPaymentsClient.achPaymentsCreatePayment({
            ...template.toPaymentDto(template),
            applyUpdatesToBatch,
            duplicatePreventionId,
        } as unknown as AchPaymentModelDto);

        return new AchPayment(dto.data.payment as unknown as AchPaymentModelDto);
    }

    public async updatePayment(template: AchTemplate, applyUpdatesToBatch: boolean) {
        const dto = await this.achPaymentsClient.achPaymentsPut(template.toDto().id, {
            ...template.toDto(),
            applyUpdatesToBatch,
        } as unknown as AchPaymentModelDto);

        return new AchPayment(dto.data as unknown as AchPaymentModelDto);
    }

    public async getPayments(type: string, filters: AchPaymentFilterModelDto) {
        const response = await this.achPaymentsClient.achPaymentsFilter2(filters, { type });
        return response.data.map(payment => new AchPayment(payment));
    }

    public async getPaymentsAndTotals(type: string, filters: AchPaymentFilterModelDto) {
        const response = await this.achClient.achPaymentsSearchAchPayments(filters, { type });
        return {
            payments: response.data.results?.map(payment => new AchPayment(payment)),
            totals: response.data.totals?.map(total => new AchPaymentTotals(total)),
        };
    }

    public async getRecipients(filters: AchRecipientFilterModelDto) {
        const response = await this.achRecipientsClient.achRecipientsPost(filters);
        const { limitedPayments, limitedBatches, achRecipientRecords } = response.data;

        return {
            achRecipients: achRecipientRecords
                ? achRecipientRecords.map(recipient => new AchRecipientRecord(recipient))
                : undefined,
            limitedPayments,
            limitedBatches,
        };
    }

    public async getAchReturnActivity(filters: AchReturnFilter) {
        const response = await this.achClient.achReturnsSearchAchReturns(filters.toDto());
        return {
            achReturns: response.data.returns
                ? response.data.returns.map(achReturn => new AchReturnActivity(achReturn))
                : [],
            totalCount: response.data.totalCount,
        };
    }

    public async downloadAchReturn(
        achReturnTransactionUniqueId: string,
        request: GetAchReturnDownloadRequestDto
    ) {
        return this.achClient.achReturnsGetAchReturnDownload(achReturnTransactionUniqueId, request);
    }

    public async downloadAchReturnActivityReport(request: SearchAchReturnDownloadRequestDto) {
        return this.achClient.achReturnsSearchAchReturnDownload(request);
    }

    public async createTemplate(template: AchTemplate) {
        const response = await this.batchClient.achBatchesPost(template.toDto());
        return new AchTemplate(response.data.batch);
    }

    public async updateTemplate(template: AchTemplate) {
        const response = await this.batchClient.achBatchesPut(
            template.templateId,
            template.toDto()
        );
        return new AchTemplate(response.data.batch);
    }

    public async validateTemplateName(name: string) {
        const response = await this.batchClient.achBatchesValidateTemplateName({
            templateName: name,
        });
        return response.data;
    }

    public async cloneTemplate(cloneId: number) {
        const response = await this.batchClient.achBatchesClone(cloneId);
        return new AchTemplate(response.data.batch);
    }

    public async approveTemplate(template: AchTemplate, comments?: string) {
        const response = await this.batchClient.achBatchesApprove(template.templateId, {
            key: template.templateId as unknown as string,
            value: comments,
            securityMessage: {
                actionType: 'Approve Batch',
                hasAlternate: false,
                challengeMethodTypeId: 0,
            },
            id: 0,
            updatedDate: '',
        });
        return response.data;
    }

    public async rejectTemplate(template: AchTemplate, comments?: string) {
        const response = await this.batchClient.achBatchesReject(template.templateId, {
            key: template.templateId as unknown as string,
            value: comments,
            securityMessage: {
                actionType: 'Reject Batch',
                hasAlternate: false,
                challengeMethodTypeId: 0,
            },
            id: 0,
            updatedDate: '',
        });
        return response.data;
    }

    public async approveAchPayments(ids: string[], comments: string) {
        return this.achPaymentsClient.achPaymentsBatch(
            {
                lookups: ids.map(id => ({
                    key: id,
                    value: comments,
                    id: 0,
                    updatedDate: '',
                })),
                securityMessage: {
                    actionType: 'Approve ACH Payment',
                    status: undefined,
                    methodUsed: undefined,
                    hasAlternate: false,
                    errorCode: undefined,
                    message: undefined,
                    oneTimePassword: undefined,
                    challengeMethodTypeId: 0,
                },
                id: 0,
                updatedDate: '',
            },
            { action: 'Approve' }
        );
    }

    public async rejectAchPayments(ids: string[], comments: string) {
        return this.achPaymentsClient.achPaymentsBatch(
            {
                lookups: ids.map(id => ({
                    key: id,
                    value: comments,
                    id: 0,
                    updatedDate: '',
                })),
                securityMessage: {
                    actionType: 'Reject ACH Payment',
                    status: undefined,
                    methodUsed: undefined,
                    hasAlternate: false,
                    errorCode: undefined,
                    message: undefined,
                    oneTimePassword: undefined,
                    challengeMethodTypeId: 0,
                },
                id: 0,
                updatedDate: '',
            },
            {
                action: 'Reject',
            }
        );
    }

    // ACH File Activity methods

    public async getAchFileActivity(
        filters: AchFileActivityFilter | { toDto(): AchFileActivityFilterModelDto }
    ) {
        const response = await this.achFileActivityClient.achFileActivityPostAll(filters.toDto());
        return response.data.map(dto => new AchFileActivity(dto));
    }

    public async getAchFileContents(fileArchiveId: number) {
        const response =
            await this.achFileActivityClient.achFileActivityGetAchFileContent(fileArchiveId);
        return new AchFile(response.data);
    }

    public async getAchFileErrorDetails(fileArchiveId: number) {
        const response =
            await this.achFileActivityClient.achFileActivityGetAchFileError(fileArchiveId);
        const errorModel = new AchFileError(response.data);
        return errorModel.validationErrors;
    }

    public async getAchFileSources() {
        const response = await this.achFileActivityClient.achFileActivityGetAchSourceList();
        return response.data.map(dto => new AchSource(dto));
    }

    public async getAchProcessedByUsers() {
        const response =
            await this.achFileActivityClient.achFileActivityGetProcessedByFilterUsers();
        return response.data;
    }

    public async deleteAchFile(fileId: number) {
        return this.achFileActivityClient.achFileActivityDelete(fileId);
    }

    public async processAchFile(fileId: number) {
        const response = await this.achFileActivityClient.achFileActivityPost(fileId);
        return new AchPayment(response.data);
    }

    public async downloadNachaFileContent(fileArchiveId: number) {
        return this.achFileActivityClient.achFileActivityGetNachaFileContent(fileArchiveId);
    }

    // Child Support methods

    public async getChildSupportPaymentById(paymentId: number) {
        const response = await this.achPaymentsClient.achPaymentsGetChildSupportPayment(
            { paymentId },
            { maxAgeInSeconds: 0 }
        );
        return new ChildSupportPayment(response.data.payment as unknown as ChildSupportPayment2Dto);
    }

    public async createChildSupportPayment(payment: ChildSupportPayment2Dto) {
        const response = await this.achPaymentsClient.achPaymentsCreateChildSupportPayment(
            payment,
            {}
        );
        return new ChildSupportPayment(response.data.payment as unknown as ChildSupportPayment2Dto);
    }

    public async updateChildSupportPayment(id: number, payment: ChildSupportPayment2Dto) {
        const response = await this.achPaymentsClient.achPaymentsChildSupportPayment(
            id,
            payment,
            {}
        );
        return new ChildSupportPayment(
            response.data.childSupportAchPayment as unknown as ChildSupportPayment2Dto
        );
    }

    public async uploadChildSupportNachaFile(file: File) {
        // Build form data for the upload
        const formData = new FormData();
        formData.append('file', file);
        return this.achPaymentsClient.achPaymentsUploadChildSupport({
            body: formData as unknown as void,
        } as unknown as Parameters<typeof this.achPaymentsClient.achPaymentsUploadChildSupport>[0]);
    }

    public async getChildSupportTemplateById(id: string) {
        const response = await this.achClient.achTemplateGetChildSupport(id, {
            maxAgeInSeconds: 0,
        });
        return new ChildSupportTemplate(response.data);
    }

    public async createChildSupportTemplate(request: CreateAchChildSupportTemplateRequestDto) {
        const response = await this.achClient.achTemplateCreateChildSupport(request, {});
        return response.data;
    }

    public async updateChildSupportTemplate(request: UpdateAchChildSupportTemplateRequestDto) {
        const response = await this.achClient.achTemplateUpdateChildSupport(request, {});
        return response.data;
    }

    public async deleteChildSupportTemplate(id: string) {
        const response = await this.achClient.achTemplateDeleteChildSupport(id, {});
        return response.data;
    }

    public async searchChildSupportTemplates(request: SearchAchChildSupportTemplateRequestDto) {
        const response = await this.achClient.achTemplateSearchChildSupport(request, {
            maxAgeInSeconds: 600,
        });
        return (response.data.templates ?? []).map(
            t => new ChildSupportTemplate(t as unknown as GetAchChildSupportTemplateResponseDto)
        );
    }

    public async downloadChildSupportTemplateDetails(
        templateId: string,
        outputType: OutPutTypeDto
    ) {
        return this.achClient.achTemplateDownloadChildSupportTemplateDetails(
            { filterModel: templateId, outputType, isPortrait: false },
            {}
        );
    }

    public async downloadChildSupportTemplateListing(
        request: SearchAchChildSupportTemplateRequestDto,
        outputType: OutPutTypeDto
    ) {
        return this.achClient.achTemplateDownloadChildSupportTemplateListing(
            { filterModel: request, outputType, isPortrait: false },
            {}
        );
    }

    public async getAchBanks() {
        const response = await this.achBanksClient.achBanksGet({ maxAgeInSeconds: 600000 });
        return response.data;
    }

    public async getChildSupportAchCompanies() {
        const dtos = (await this.companyClient.achCompaniesGet({ maxAgeInSeconds: 600 })).data;
        return dtos
            .filter(
                c =>
                    c.batchBalanceRequirements === 'Unbalanced - Full Offset' &&
                    (c as unknown as { secCodes: { code: string }[] }).secCodes?.some(
                        sc => sc.code === 'CCD'
                    ) &&
                    c.notOnUsTransactionTypes === 'Credit Only'
            )
            .map(c => new AchCompany(c));
    }

    public async getAchNoticeOfChange(filter: SearchAchNotificationOfChangeRequestDto) {
        const response =
            await this.achClient.achNotificationOfChangeSearchAchNotificationOfChange(filter);
        return response.data?.notificationOfChanges?.map(dto => new AchNoticeOfChange(dto)) ?? [];
    }

    public async downloadNocNotification(
        nocTransactionUniqueId: string,
        request: GetAchNotificationOfChangeDownloadRequestDto
    ) {
        const response = await this.achClient.achNotificationOfChangeGetAchDownload(
            nocTransactionUniqueId,
            request
        );
        const arrayBuffer = await (response as unknown as Response).arrayBuffer();
        const filename =
            (response as unknown as Response).headers?.get?.('x-filename') ??
            `ACH-Notification-of-Change-${nocTransactionUniqueId}`;
        const contentType =
            (response as unknown as Response).headers?.get?.('content-type') ??
            'application/octet-stream';
        const blob = new Blob([arrayBuffer], { type: contentType });
        return downloadBlob(blob, filename);
    }

    public async downloadNocReport(request: SearchAchNotificationOfChangeDownloadRequestDto) {
        return this.achClient.achNotificationOfChangeSearchAchNotificationOfChangeDownload(request);
    }

    // Master Recipient methods

    public async getMasterRecipients(
        filter: GetMasterRecipientsRequestDto
    ): Promise<{ recipients: MasterRecipient[]; totalCount: number }> {
        const response =
            await this.achMasterRecipientClient.achMasterRecipientGetMasterRecipients(filter);
        const data = response.data as GetMasterRecipientsResponseDto;
        const items = data?.pagedRecipients?.items ?? [];
        const totalCount = data?.pagedRecipients?.totalCount ?? 0;
        return {
            recipients: items.map((item: AchRecipientDto) => {
                const transactionTypeId = item.transactionTypeId ?? 1;
                const transactionType =
                    item.transactionTypeAbbreviation ?? (transactionTypeId === 1 ? 'CR' : 'DR');

                return new MasterRecipient({
                    id: item.id ?? 0,
                    recipientName: item.recipientName ?? '',
                    recipientIdNumber: item.recipientIdNumber ?? undefined,
                    accountNumber: item.accountNumber ?? '',
                    accountTypeDescription: item.accountTypeDescription ?? 'Checking',
                    accountTypeId: item.accountTypeId ?? 1,
                    routingNumber: item.routingNumber ?? '',
                    transactionType,
                    transactionTypeId,
                    defaultAmount: item.defaultAmount ?? 0,
                    addenda: item.addenda ?? undefined,
                    achMasterRecipientStatusTypeDescription:
                        item.achMasterRecipientStatusTypeDescription ?? '',
                    achMasterRecipientStatusTypeId: item.achMasterRecipientStatusTypeId ?? 0,
                    approvalCount: item.approvalCount ?? 0,
                    requiredApprovals: item.requiredApprovals ?? 1,
                    canApproveReject: item.canApproveReject ?? false,
                    canEdit: item.canEdit ?? true,
                    updatedBy: item.updatedBy ?? 0,
                });
            }),
            totalCount,
        };
    }

    public async saveMasterRecipients(recipients: SaveableMasterRecipientDto[]) {
        return this.achMasterRecipientClient.achMasterRecipientSaveMasterRecipient({
            saveableMasterRecipients: recipients,
        });
    }

    public async approveMasterRecipients(ids: number[]) {
        return this.achMasterRecipientClient.achMasterRecipientApproveMasterRecipients({
            achMasterRecipientIds: ids,
        });
    }

    public async rejectMasterRecipients(ids: number[]) {
        return this.achMasterRecipientClient.achMasterRecipientRejectMasterRecipients({
            achMasterRecipientIds: ids,
        });
    }

    public async deleteMasterRecipients(ids: number[]) {
        return this.achMasterRecipientClient.achMasterRecipientDeleteMasterRecipients({
            achMasterRecipientIds: ids,
        });
    }

    public async uploadMasterRecipients(file: File, format: string) {
        return this.achRecipientsClient.achRecipientsPostAll({ file }, { format });
    }

    public async getMasterRecipientAudits(
        recipientId: number
    ): Promise<{ action: string; actionDate: string; userAlias: string }[]> {
        const response =
            await this.achMasterRecipientClient.achMasterRecipientGetAudits(recipientId);
        const data = response.data as {
            audits?: { action: string; actionDate: string; userAlias: string }[];
        };
        return data?.audits ?? [];
    }

    private toInternationalTemplateRequest(
        filters: AchBatchFilter
    ): SearchAchInternationalTemplateRequestDto {
        const dto = filters.toDto() as AchBatchFilterModelDto;
        const statusMap: Record<string, AchTemplateStatusTypeDto> = {
            Ready: AchTemplateStatusTypeDto.Ready,
            'Pending Approval': AchTemplateStatusTypeDto.PendingApproval,
            'Approval Rejected': AchTemplateStatusTypeDto.Rejected,
        };
        const amountTypeMap: Record<string, AchFilterAmountTypeDto> = {
            'Specific Amount': AchFilterAmountTypeDto.SpecificAmount,
            Range: AchFilterAmountTypeDto.Range,
        };
        const dateTypeMap: Record<string, AchFilterDateTypeDto> = {
            'Specific Date': AchFilterDateTypeDto.SpecificDate,
            Range: AchFilterDateTypeDto.Range,
        };
        const status = statusMap[dto.status] ?? (0 as unknown as AchTemplateStatusTypeDto);
        const debitAmountType =
            amountTypeMap[dto.debitAmountType] ?? AchFilterAmountTypeDto.Undefined;
        const creditAmountType =
            amountTypeMap[dto.creditAmountType] ?? AchFilterAmountTypeDto.Undefined;
        const lastUpdatedDateType =
            dateTypeMap[dto.updatedDateType] ?? AchFilterDateTypeDto.Undefined;
        return {
            achCompanyIdList: dto.achCompanyList?.map(c => c.id) ?? [],
            templateName: dto.batchName || undefined,
            status,
            secCode: AchSecCodeTypeDto.IAT,
            lastUpdatedDateType,
            lastUpdated: dto.updatedDate || undefined,
            lastUpdatedEnd: dto.lastUpdatedEnd || undefined,
            debitAmountType,
            debitAmount: dto.debitAmount || undefined,
            debitAmountMax: dto.debitAmountMax || undefined,
            creditAmountType,
            creditAmount: dto.creditAmount || undefined,
            creditAmountMax: dto.creditAmountMax || undefined,
        };
    }

    private mapTemplateResponseToBatchDto(
        template: SearchAchTemplateBaseResponseDto
    ): AchBatchModelDto {
        const raw = template as Record<string, unknown>;
        return {
            id: template.templateIntId ?? 0,
            name: template.templateName ?? '',
            achCompanyName: template.achCompanyName ?? '',
            achCompanyId: '',
            secCode: template.secCode ?? 'IAT',
            discretionaryData: '',
            entryDescription: '',
            restricted: false,
            lastUpdated: template.lastUpdated ?? '',
            debitAmount: (raw.debitAmount as number) ?? 0,
            creditAmount: (raw.creditAmount as number) ?? 0,
            numberOfApprovalsNeeded: template.numberOfApprovalsNeeded ?? 0,
            completedApprovalCount: template.completedApprovalCount,
            status: template.status ?? '',
            successMessage: '',
            achCompany: {} as AchBatchModelDto['achCompany'],
            frequency: {} as AchBatchModelDto['frequency'],
            offsetAccount: { id: 0 },
            permissions: (template.userPermissions ?? []).map(p => ({
                id: 0,
                permission: p,
            })),
        };
    }
}
